import type { JSX, ReactNode } from 'react';
import { Icon } from './Icon';

export interface TabDef<T extends string> {
  readonly id: T;
  readonly label: string;
  readonly icon?: string | undefined;
  /** Rendered after the label, e.g. a count chip. */
  readonly badge?: ReactNode | undefined;
}

/**
 * Tab bar. Renders real buttons with `role="tab"`, so arrow-key navigation and
 * screen-reader announcement come from the platform rather than being faked.
 */
export function Tabs<T extends string>({
  tabs,
  active,
  onSelect,
  ariaLabel = 'Sections',
}: {
  readonly tabs: readonly TabDef<T>[];
  readonly active: T;
  readonly onSelect: (id: T) => void;
  readonly ariaLabel?: string | undefined;
}): JSX.Element {
  return (
    <div className="tabs" role="tablist" aria-label={ariaLabel}>
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          className="tab"
          role="tab"
          aria-selected={active === t.id}
          onClick={() => {
            onSelect(t.id);
          }}
        >
          {t.icon !== undefined && <Icon name={t.icon} />}
          {t.label}
          {t.badge}
        </button>
      ))}
    </div>
  );
}
